import { useRef } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useApolloClient } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import {
  FIRST_PAGE_NUMBER,
  PRODUCTS_PAGE_SIZE,
} from '../../consts/defaultConfigValues/defaultConstants'
import { useLazyGetCatalog } from '../../graphQL/queries/getCatalog'
import { getCategoryProductsQuery } from '../../graphQL/queries/getCatalog/query'
import {
  ICatalogItem as ICatalogItemHeadless,
  ICategoryProducts,
  ICategoryProductsVariables,
} from '../../graphQL/queries/getCatalog/types'
import { useLazyGetCart } from '../../graphQL/queries/getCart'
import { useLazyGetSession } from '../../graphQL/queries/getSession'
import { SessionDataArgs } from '../../graphQL/queries/getSession/types'
import { ICatalogItem } from '../../interfaces'
import {
  setCategoriesAndPagination,
  setRepriced,
  setRepricing,
} from '../../redux/slices/catalog'
import { AppDispatch, RootState } from '../../redux/store'
import { setMessage, setShow } from '../../redux/slices/globalPromotions'
import { castICatalogItems } from '../../utils/castICatalogItems'
import { setCatalogData } from '../../utils/categoryUtils'
import { generateDataDogContext } from '../../utils/dataDog'
import { onCartResponse } from '../../utils/onCartResponse'
import updateCurrentCategory from '../../utils/updateCurrentCategory'
import {
  getFlattedCartItems,
  setItemsQuantity,
} from '../../utils/utilsForCatalogReducer'
import { settledCartWrites } from '../../utils/settledCartWrites'
import { settlePendingCartWrites } from '../../utils/pendingCartWrites'
import { getMultipleTotalPromotionsLabel } from '../../utils/promotions/getTotalPromotionsLabel'
import { writeSessionData } from '../../utils/writeSessionData'
import { castSuggestedProducts } from '../suggestedItems/useSuggestedItems'
import { useLazyGetSuggestedItems } from '../../graphQL/queries/suggestedItems'
import { setSuggestedProducts } from '../../redux/slices/suggestedProducts'
import useAppDispatch from '../useAppDispatch'
import useAppStore from '../useAppStore'

const FUNCTION_NAME = 'useRefreshCatalog'

/*
 * Apollo shares one in-flight request between identical queries. A refresh
 * started while an earlier one is still out would get the earlier answer,
 * which may predate the reprice.
 */
const FRESH_READ = { queryDeduplication: false }

/**
 * How many times the cart is read again because a cart write answered while
 * the previous read was out. Past it the refresh writes the catalogue with
 * the cart the store already holds (`writeCatalogWithStoreCart`).
 */
const MAX_CART_REREADS = 2

interface ICategoryKeyArgs {
  category?: string | null
  parentId?: string | null
}

const categoryKey = ({ category, parentId }: ICategoryKeyArgs) =>
  JSON.stringify([parentId ?? null, category ?? null])

interface IRoundUpToPageArgs {
  depth: number
  pageSize: number
}

/** The smallest whole number of pages that holds `depth` products. */
const roundUpToPage = ({ depth, pageSize }: IRoundUpToPageArgs) =>
  Math.ceil(depth / pageSize) * pageSize

/**
 * Cuts each refetched category back to the depth the shopper had loaded,
 * rounded up to a whole page and never below one page (COM-1520).
 *
 * Rounded up because `setCatalogPagination` derives the next page from the
 * length it is given (`pageNumber = ceil(length / pageSize)`, `hasMore` only
 * on a page boundary), and load-more asks for `pageNumber + 1` at the page
 * size. A cut that is not on a boundary is read as a fully loaded category:
 * one loaded at 25 of 20-product pages that now has 32 would keep 25, read
 * `hasMore: false`, and never show the other 7. So what is kept is either a
 * whole number of pages or everything the read returned.
 *
 * The reads ask for no more than this (`deepCategoriesOf`), so on the normal
 * path it cuts nothing. It still matters when the depth in the store moved
 * while the reads were out: the depth is read when the answer lands, and a
 * cut to a page boundary keeps the pagination load-more continues from.
 * @internal
 */
export const keepLoadedDepth = (
  fresh: ICatalogItem[],
  current: ICatalogItem[],
  pageSize: number,
): ICatalogItem[] => {
  const depth = new Map(
    current.map((item) => [categoryKey(item), item.productsPrices?.length]),
  )
  return fresh.map((item) => ({
    ...item,
    productsPrices: item.productsPrices?.slice(
      0,
      Math.max(
        roundUpToPage({ depth: depth.get(categoryKey(item)) ?? 0, pageSize }),
        pageSize,
      ),
    ),
  }))
}

/** A category loaded past its first page, and what its own read asks for. */
export interface IDeepCategory {
  key: string
  /** The `category` argument, the category's name as load-more sends it. */
  categoryName: string
  /** The loaded depth rounded up to a whole page. */
  pageSize: number
}

interface IDeepCategoriesOfArgs {
  categories: ICatalogItem[]
  pageSize: number
}

/**
 * The categories the shopper loaded deeper than one page, each with its own
 * page size: the loaded depth rounded up to a whole page (COM-1520). Every
 * other category is served by the first-page read. A category with no name
 * cannot be asked for on its own and gets its first page back.
 * @internal
 */
export const deepCategoriesOf = ({
  categories,
  pageSize,
}: IDeepCategoriesOfArgs): IDeepCategory[] =>
  categories.flatMap((item) => {
    const depth = item.productsPrices?.length ?? 0
    if (!item.category || depth <= pageSize) {
      return []
    }
    return [
      {
        key: categoryKey(item),
        categoryName: item.category,
        pageSize: roundUpToPage({ depth, pageSize }),
      },
    ]
  })

type ProductsPrices = NonNullable<ICatalogItemHeadless['productsPrices']>

interface IWithDeepCategoriesArgs {
  /** The first-page read, one page of every category. */
  firstPage: ICatalogItemHeadless[]
  /** The products each deep category's own read returned, by category key. */
  deep: Map<string, ProductsPrices>
}

/**
 * Lays each deep category's own read over its entry in the first-page read
 * (COM-1520). The first-page read decides which categories there are and in
 * what order; a deep read only replaces its category's products. A category
 * whose own read failed keeps its first page.
 * @internal
 */
export const withDeepCategories = ({
  firstPage,
  deep,
}: IWithDeepCategoriesArgs): ICatalogItemHeadless[] =>
  firstPage.map((item) => {
    const productsPrices = deep.get(categoryKey(item))
    if (!productsPrices) {
      return item
    }
    return { ...item, productsPrices }
  })

interface IReadArgs {
  storeName: string
  sessionId: string
}

interface IReadDeepCategoryArgs extends IReadArgs {
  deepCategory: IDeepCategory
}

interface IDeepCategoryResult {
  deepCategory: IDeepCategory
  /** Absent when the read failed or did not return the category. */
  productsPrices?: ProductsPrices
  error?: unknown
}

interface IWriteCatalogWithStoreCartArgs {
  /**
   * The refetched categories, cut back to the loaded depth, with the slugs
   * the reducer derives (`setCatalogData`).
   */
  categories: ICatalogItem[]
  /** The store as it is when the answer lands. */
  state: RootState
  dispatch: AppDispatch
}

/**
 * Writes the new catalogue with the quantities of the cart the store holds,
 * and leaves that cart alone. The refresh takes this path when cart writes
 * kept answering under each of its cart reads (COM-1521): the last read may
 * be older than a write that has already answered, and every answer has
 * already written its cart to the store, so the store's cart is the newest
 * there is.
 *
 * Writes `catalogSlice.categories` and `pagination`, as `onCartResponse`
 * does, and re-points `catalogSlice.currentCategory` by slug, which the
 * caller has already derived (`setCatalogData`). Leaves the cart
 * slice, `setCartInfo`, the open product and every other list
 * `onCartResponse` writes (recommended products, filter results, suggested
 * items, buy-again and the other recommendation lists, `categoriesNavSlice`)
 * as they are: none of them is re-priced by this refresh, and their
 * quantities are already the store cart's. Opens no deferred-promotion modal.
 * The quantities are laid on as `utils/fetchProducts.ts` does for a new page.
 */
const writeCatalogWithStoreCart = ({
  categories,
  state,
  dispatch,
}: IWriteCatalogWithStoreCartArgs): void => {
  const {
    cartSlice: { items, groups, activePromotions },
    catalogSlice: { currentCategory },
    defaultSlice: { config },
  } = state
  const { flattedCartItems } = getFlattedCartItems({ items, groups })
  const { categories: withQuantities } = setItemsQuantity({
    categories,
    flattedCartItems,
    config,
    activePromotions,
  })
  dispatch(
    setCategoriesAndPagination({
      categories: withQuantities,
      paginationSize: config?.options?.paginationSize,
    }),
  )
  updateCurrentCategory({
    categories: withQuantities,
    currentCategory,
    dispatch,
  })
}

/**
 * The handler behind `TemplateActionNames.CATALOG_REFRESH_CATALOG` (COM-1500): reads
 * the session, the catalog and the cart again for the current session and
 * writes them through `onCartResponse`, with no page reload, so a Workflow
 * step that changed the customer type shows its new prices.
 *
 * - The session is read again (COM-1520) and written as boot writes it
 *   (`writeSessionData`: `setSessionData`, then the checkout-rule messages
 *   cast to the language), before the catalogue is cast, so the prices are
 *   baked against the `hidePricesActive` of the customer as they are now and
 *   the session snapshot (the customer's custom fields, the checkout-rule
 *   picks) is the new one. It goes out beside the other reads rather than
 *   before them: nothing is cast until all of them land, so a read first
 *   would only add a round trip. A session read that fails or returns
 *   nothing, or a session whose write throws, is reported on its own, and
 *   the catalogue is cast against the config the store then holds; the
 *   refresh still counts as done. The session is written only by an attempt
 *   that writes the catalogue, so an overtaken or failed attempt changes
 *   nothing.
 * - Cart writes still waiting in their debounce, and any queued while it
 *   waits, are sent first, and the refetch waits for them (one bound for the
 *   whole drain, `settlePendingCartWrites`), so a tap made just before is in
 *   the cart it reads. Only the add and remove writes are waited for. Any
 *   cart write that answers while the read is out, those or a delete,
 *   clear-cart, prefill or chosen-free-goods write (`settledCartWrites`,
 *   COM-1512), makes it read the cart again (`MAX_CART_REREADS`), so an older
 *   cart never goes back over a newer one. If writes keep answering past the
 *   last re-read, the new catalogue is still written, with the quantities of
 *   the cart the store holds, and that cart is left as it is
 *   (`writeCatalogWithStoreCart`, COM-1521). That is reported to Datadog and
 *   the refresh counts as done.
 * - Each drain waits at most `PENDING_CART_WRITES_WAIT_MS` (COM-1521). A
 *   write in flight for 15 s (`PRESUMED_HUNG_CART_WRITE_MS`) is given up on
 *   and reported as presumed hung, so a refresh or reload that starts, or
 *   begins a drain round, once it is that old does not wait for it; one
 *   already waiting on it finishes its own bound. A slow, healthy write
 *   younger than that, a + sent during an earlier wait included, is still
 *   waited for (`settlePendingCartWrites`).
 * - The catalog and the session are read with `no-cache`: their hooks set no
 *   policy, so a same-variables read would answer from the cache with the
 *   boot answer. `no-cache` rather than `network-only` so the answer is not
 *   written over the boot query's cached result, which the boot hook still
 *   watches. Every read also skips Apollo's deduplication (`FRESH_READ`),
 *   and an attempt that a newer one has overtaken writes nothing. A read
 *   that answers with an `error` counts as no answer, even when it carries
 *   `data`: a failed lazy execute resolves with the hook's last result,
 *   which on a second refresh is the first refresh's answer.
 * - The pages the Apollo cache holds keep the old prices, so once this has
 *   written new ones it sets `catalogSlice.repriced`, and load-more reads
 *   with `no-cache` from then on (`useProductsLazyLoader`). Without it a
 *   category this cut back would load its next page from the cache at the
 *   old prices.
 * - It raises no loader. `routes/index.tsx` counts any loader it does not
 *   exempt as booting and unmounts the screen while it is up, and
 *   `INITIAL_DATA` is a boot loader.
 * - Each category keeps the depth the shopper loaded, rounded up to a whole
 *   page, and `onCartResponse` keeps the cart quantities on the counters
 *   (COM-1520). One catalog read asks for the first page of every category
 *   at the configured page size; each category loaded deeper than that gets
 *   a read of its own (`getCategoryProductsQuery`, which asks for no total
 *   promotions), for its depth rounded up to a whole page
 *   (`deepCategoriesOf`), whose products replace its first page
 *   (`withDeepCategories`). So a category that grew under the new customer
 *   type keeps what it gained up to that boundary, and load-more continues
 *   from the next page (`keepLoadedDepth`). A deep read that fails, or does
 *   not return its category, is reported and leaves that category at its
 *   first page, from which load-more continues. A page loaded while the
 *   reads were out is not kept: the category gets the fresh answer, and
 *   load-more asks for the next page after it. Both write
 *   paths re-point the open category by slug, so the categories get the
 *   reducer's slugs before either write (PR #843 review): the catalog query
 *   selects none, and a match on none cleared the open category, after which
 *   the next lazy-loaded page threw on `pagination['']`.
 * - The catalog reads use `errorPolicy: 'ignore'`, as at boot, so a failing
 *   `getTotalPromotions` does not discard the catalog. An answer with no total
 *   promotions takes the total-promotions banner down; a failed one leaves it
 *   as it was, and an unchanged label does not reopen a banner the shopper
 *   closed.
 * - The catalogue's recommendations carousel (`suggestedItems`) is read again
 *   with its last variables, if it was fetched at all, after the catalogue is
 *   written and only by the newest attempt (`refreshSuggestedItems`).
 * - `catalogSlice.repricing` is up only around each write of new prices,
 *   which is what makes the memoised cards re-render (`writeRepricing`).
 * - A failure leaves what is on screen as it was and reports to Datadog. No
 *   error screen.
 *
 * Re-priced: `catalogSlice.categories`, `suggestedProductsSlice` and the cart;
 * not the cart when the cart kept changing under the reads (see above).
 * Re-read but left alone: the session's banners and advanced filters, which
 * boot also takes from the session and this does not write.
 * Left with their old prices: the open product (`catalogSlice.selectedProduct`),
 * search results, filter results, `categoriesNavSlice`, the cart's
 * frequently-bought-together list and the buy-again, previously-purchased,
 * recommended-for-you and alternative-products lists. The last four have
 * their own `no-cache` fetch actions to list beside this one.
 *
 * Do not list `templateActions.cart.refreshCart` beside it: this already reads
 * the cart, and `useGetCart` writes from a render-time snapshot, so its answer
 * landing after this one's could put the old catalogue prices back.
 */
export const useRefreshCatalog = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const client = useApolloClient()
  const attemptRef = useRef(0)
  const [getCatalog] = useLazyGetCatalog()
  const [getCart] = useLazyGetCart()
  const [getSession] = useLazyGetSession()
  const [getSuggestedItems] = useLazyGetSuggestedItems()

  const report = (error: unknown, extraInfo: Record<string, unknown>) => {
    const context = generateDataDogContext({
      title: 'refreshCatalog failed; the catalogue keeps its old prices',
      extraInfo: { function: FUNCTION_NAME, ...extraInfo },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  /**
   * Runs one synchronous write of new prices with `catalogSlice.repricing`
   * up, so memoised cards re-render. Raised here rather than for the whole
   * refresh, so memoisation stays on while the reads are out. Lowered on a
   * later task, so the render of the write still sees it up, and by every
   * write, so no exit path can leave it up.
   *
   * Raised and written in one batch. `index.tsx` mounts a legacy root, where
   * each dispatch outside a React handler renders on its own, so a separate
   * raise drew every unmemoised row once more with the old prices (PR #850
   * review). react-redux 9's `batch` is a no-op, hence react-dom's.
   */
  const writeRepricing = (write: () => void) => {
    try {
      unstable_batchedUpdates(() => {
        dispatch(setRepricing(true))
        write()
      })
    } finally {
      setTimeout(() => dispatch(setRepricing(false)), 0)
    }
  }

  /**
   * The cart kept changing under every read, so the catalogue was written
   * with the store's cart instead (COM-1521). Not a failure: the new prices
   * are on screen.
   */
  const reportStaleCart = (rereads: number) => {
    const context = generateDataDogContext({
      title:
        'refreshCatalog wrote the catalogue with the store cart; the cart kept changing under it',
      extraInfo: { function: FUNCTION_NAME, rereads },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error('refreshCatalog cart kept changing under it'),
      context,
    )
  }

  const reportSession = (error: unknown) => {
    const context = generateDataDogContext({
      title:
        'refreshCatalog could not read the session again; the prices were cast against the config the store holds',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const reportSessionWrite = (error: unknown) => {
    const context = generateDataDogContext({
      title:
        'refreshCatalog could not write the session it read; the prices were cast against the config the store holds',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const reportDeepFallback = (failures: IDeepCategoryResult[]) => {
    const context = generateDataDogContext({
      title:
        'refreshCatalog could not read a category to its loaded depth; it kept the first page',
      extraInfo: {
        function: FUNCTION_NAME,
        categories: failures.map(({ deepCategory }) => deepCategory.categoryName),
        pageSizes: failures.map(({ deepCategory }) => deepCategory.pageSize),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      failures[0].error ??
        new Error('refreshCatalog got no products for a deep category'),
      context,
    )
  }

  /**
   * The session as it is now, or `undefined` with the reason. Never rejects,
   * so a failed session read cannot fail the refresh.
   *
   * An `error` wins over `data`: a lazy execute that fails resolves with the
   * hook's current result (`useLazyQuery`'s `executeQuery`, Apollo 3.12),
   * which still holds the previous refresh's session. Taking it would skip
   * the report and write that older snapshot back over `sessionData`.
   */
  const readSession = async ({ storeName, sessionId }: IReadArgs) => {
    try {
      const { data, error } = await getSession({
        variables: { storefrontName: storeName, sessionUid: sessionId },
        fetchPolicy: 'no-cache',
        context: FRESH_READ,
      })
      return { session: error ? undefined : data?.session, error }
    } catch (exception) {
      return { session: undefined, error: exception }
    }
  }

  /**
   * One deep category, read on its own to its loaded depth rounded up to a
   * whole page, with the variables load-more (`utils/fetchProducts.ts`) sends
   * for that category, so the page boundaries are the ones load-more then
   * continues from. Never rejects: a failure falls back to the first page.
   *
   * On `client.query` rather than the lazy `getCatalog`: several of these go
   * out at once, and a lazy query's execute re-drives one observable, so
   * concurrent calls with different variables are not independent (the same
   * reason `promotionItemsAvailability` gives). The category is picked out
   * of the answer by name and parent, since the answer also carries its
   * subcategories, and a name shared by two subcategories may answer with the
   * other one; that counts as a failure. `getCategoryProductsQuery` selects
   * the same products as the first-page read, so they cast alike, and no
   * total promotions, which only the first-page read's answer is used for.
   */
  const readDeepCategory = async ({
    deepCategory,
    storeName,
    sessionId,
  }: IReadDeepCategoryArgs): Promise<IDeepCategoryResult> => {
    try {
      const { data, error } = await client.query<
        ICategoryProducts,
        ICategoryProductsVariables
      >({
        query: getCategoryProductsQuery,
        variables: {
          groupCategories: true,
          catalogStorefrontName: storeName,
          catalogSessionId: sessionId,
          categoryName: deepCategory.categoryName,
          pagination: {
            pageNumber: FIRST_PAGE_NUMBER,
            pageSize: deepCategory.pageSize,
          },
        },
        fetchPolicy: 'no-cache',
        // As the first-page read: an error in one field must not discard the
        // category; a category missing from the answer is still a failure
        errorPolicy: 'ignore',
        context: FRESH_READ,
      })
      const entry = data?.catalog?.find(
        (item) => categoryKey(item) === deepCategory.key,
      )
      if (!entry?.productsPrices) {
        return {
          deepCategory,
          error:
            error ?? new Error('refreshCatalog got no products for the category'),
        }
      }
      return { deepCategory, productsPrices: entry.productsPrices }
    } catch (exception) {
      return { deepCategory, error: exception }
    }
  }

  /**
   * Writes the session as boot does (`writeSessionData`), and reports rather
   * than throws when that write fails: `setSessionData` reads
   * `checkoutRules.cartConditionPendingMinAmount.isActive` unguarded, so a
   * session missing that rule throws, and a session problem must not fail
   * the refresh. A reducer that throws leaves its slice as it was, so the
   * catalogue is then cast against whatever config the store holds.
   */
  const writeSession = (session: SessionDataArgs) => {
    try {
      writeSessionData({
        session,
        dispatch,
        localization: store.getState().defaultSlice.config?.options?.localization,
      })
    } catch (exception) {
      reportSessionWrite(exception)
    }
  }

  const reportSuggested = (error: unknown) => {
    const context = generateDataDogContext({
      title: 'refreshCatalog could not re-price the suggested items',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  /**
   * Repeats the carousel's last `suggestedItems` read, once the catalogue has
   * been written, and writes its prices only if this attempt is still the
   * newest. A read of its own rather than `useSuggestionItems`, so the
   * carousel's status never goes to loading or error during a refresh, and
   * the cart quantities are merged from the store as it is when the answer
   * lands. A failure here is reported as its own and leaves the carousel as
   * it was; the catalogue's new prices stand.
   */
  const refreshSuggestedItems = async (attempt: number) => {
    try {
      const { request } = store.getState().suggestedProductsSlice
      if (!request) {
        return
      }
      const { storefrontName, sessionUid, limit } = request
      const { data, error } = await getSuggestedItems({
        variables: { storefrontName, sessionUid, limit: +limit || 1000 },
        // Not `no-cache`: the carousel's own read is cache-first, so a
        // carousel mounted later (catalog -> cart -> catalog) would write the
        // boot answer, and its old prices, back over this one. Writing the
        // cache does not re-fire the mounted carousel's `onCompleted`: Apollo
        // calls it only when the network status moves to ready (PR #850)
        fetchPolicy: 'network-only',
        context: FRESH_READ,
      })
      if (attempt !== attemptRef.current) {
        return
      }
      if (!data?.suggestedItems) {
        reportSuggested(error ?? new Error('refreshCatalog got no suggestions'))
        return
      }
      const {
        defaultSlice: { config },
        cartSlice: { items, groups, activePromotions },
      } = store.getState()
      const products = castSuggestedProducts({
        suggestedItems: data.suggestedItems,
        config,
        items,
        groups,
        activePromotions,
      })
      writeRepricing(() => dispatch(setSuggestedProducts(products)))
    } catch (exception) {
      reportSuggested(exception)
    }
  }

  const refreshCatalog = async (): Promise<boolean> => {
    attemptRef.current += 1
    const attempt = attemptRef.current
    try {
      await settlePendingCartWrites(FUNCTION_NAME)
      const {
        defaultSlice: { sessionId, storeName, config },
        catalogSlice: { categories },
      } = store.getState()
      if (!sessionId || !storeName) {
        report(new Error('refreshCatalog has no session'), {})
        return false
      }
      const pageSize = config?.options?.paginationSize || PRODUCTS_PAGE_SIZE
      const deepCategories = deepCategoriesOf({ categories, pageSize })
      const readCart = () =>
        getCart({
          variables: {
            cartStorefrontName: storeName,
            cartSessionUid: sessionId,
          },
          context: FRESH_READ,
        })
      // Taken as the reads go out, see the re-read below
      let settledAtRead = settledCartWrites()
      const [catalogResult, firstCartResult, sessionResult, deepResults] =
        await Promise.all([
          getCatalog({
            variables: {
              showChildrenCategories: true,
              catalogStorefrontName: storeName,
              catalogSessionId: sessionId,
              totalPromotionsSessionId: sessionId,
              // One page of every category; deeper ones are read on their own
              pagination: { pageNumber: FIRST_PAGE_NUMBER, pageSize },
              groupCategories: true,
            },
            fetchPolicy: 'no-cache',
            // As at boot (`useInitialCatalogData`): a failing field beside
            // `catalog`, such as `getTotalPromotions`, must not discard the
            // whole answer. A missing catalog is still caught below
            errorPolicy: 'ignore',
            context: FRESH_READ,
          }),
          readCart(),
          readSession({ storeName, sessionId }),
          Promise.all(
            deepCategories.map((deepCategory) =>
              readDeepCategory({ deepCategory, storeName, sessionId }),
            ),
          ),
        ])
      if (attempt !== attemptRef.current) {
        return false
      }
      /*
       * A cart write whose answer landed while the cart read was out (a +,
       * a delete, a clear, a prefill, a chosen free good) has already
       * written a newer cart than that read can hold; writing the read would
       * put the older cart back on screen. So read the cart again until no
       * cart write has answered in between (`settledCartWrites`, COM-1512).
       * Writes still out when the refresh writes are harmless: their own
       * answers land after it.
       */
      let cartResult = firstCartResult
      let rereads = 0
      let isCartStale = false
      while (settledCartWrites() !== settledAtRead) {
        if (rereads === MAX_CART_REREADS) {
          isCartStale = true
          break
        }
        rereads += 1
        // eslint-disable-next-line no-await-in-loop
        await settlePendingCartWrites(FUNCTION_NAME)
        settledAtRead = settledCartWrites()
        // eslint-disable-next-line no-await-in-loop
        cartResult = await readCart()
        if (attempt !== attemptRef.current) {
          return false
        }
      }
      // An answer with an `error` is no answer: a lazy execute that fails
      // resolves with the hook's last result, the previous refresh's
      // catalogue or cart (as `readSession`)
      const catalog = catalogResult.error
        ? undefined
        : catalogResult.data?.catalog
      // Not needed when the cart kept changing: the store's cart is used
      const cart = cartResult.error ? undefined : cartResult.data?.cart
      const isCartMissing = !cart && !isCartStale
      if (!catalog?.length || isCartMissing) {
        report(
          catalogResult.error ??
            cartResult.error ??
            new Error('refreshCatalog got no catalog or no cart'),
          { catalog: !!catalog?.length, cart: !!cart },
        )
        return false
      }
      if (!sessionResult.session) {
        reportSession(
          sessionResult.error ?? new Error('refreshCatalog got no session'),
        )
      }
      const deepFailures = deepResults.filter(
        ({ productsPrices }) => !productsPrices,
      )
      if (deepFailures.length) {
        reportDeepFallback(deepFailures)
      }
      const deep = new Map<string, ProductsPrices>()
      deepResults.forEach(({ deepCategory, productsPrices }) => {
        if (productsPrices) {
          deep.set(deepCategory.key, productsPrices)
        }
      })
      const catalogWithDepth = withDeepCategories({ firstPage: catalog, deep })
      // Undefined exactly when the cart kept changing, since a cart missing
      // otherwise returned above
      const freshCart = isCartStale ? undefined : cart
      if (!freshCart) {
        reportStaleCart(rereads)
      }
      writeRepricing(() => {
        // In the same batch as the prices, so no render shows the new
        // `hidePricesActive` beside the old baked prices
        if (sessionResult.session) {
          writeSession(sessionResult.session)
        }
        // Read after the session is written: the config bakes `hidePrices`
        // into the stored prices (COM-974, COM-1520), the categories carry
        // the loaded depth, and the cart is the newest any write has
        // answered with.
        const state = store.getState()
        // With the slugs the reducer derives, so both writes below re-point
        // the open category by slug: the catalog query selects none, and
        // matching on none clears it (PR #843 review)
        const freshCategories = setCatalogData(
          keepLoadedDepth(
            castICatalogItems(catalogWithDepth, state.defaultSlice.config),
            state.catalogSlice.categories,
            pageSize,
          ),
        )
        // The cache's pages predate these prices: load-more reads past it
        dispatch(setRepriced())
        if (freshCart) {
          onCartResponse({
            data: freshCart,
            categories: freshCategories,
            dispatch,
            state,
          })
          return
        }
        writeCatalogWithStoreCart({
          categories: freshCategories,
          state,
          dispatch,
        })
      })
      const currentConfig = store.getState().defaultSlice.config
      // The prices are written by now, so a failure here is reported as its
      // own and the refresh still counts as done. Unlike boot, which only
      // ever shows the banner, an answer with no total promotions takes it
      // down: the new customer type may have none. A failed field arrives as
      // `null` under `errorPolicy: 'ignore'` and leaves the banner as it was.
      // A label the banner already carries is not shown again, so a banner
      // the shopper closed stays closed unless the promotions changed
      const totalPromotions = catalogResult.data?.getTotalPromotions
      if (totalPromotions && totalPromotions.length === 0) {
        dispatch(setShow(false))
      } else if (totalPromotions) {
        try {
          const label = getMultipleTotalPromotionsLabel(
            totalPromotions,
            currentConfig,
          )
          if (label !== store.getState().globalPromotionsSlice.message) {
            dispatch(setMessage(label))
            dispatch(setShow(true))
          }
        } catch (exception) {
          const context = generateDataDogContext({
            title: 'refreshCatalog could not label the total promotions',
            extraInfo: { function: FUNCTION_NAME },
          })
          datadogRum.startView(context.viewName)
          datadogRum.addError(exception, context)
        }
      }
      await refreshSuggestedItems(attempt)
      return true
    } catch (exception) {
      report(exception, {})
      return false
    }
  }

  return refreshCatalog
}

export default useRefreshCatalog
